import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

type ThemeType = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: ThemeType;
  currentTheme: 'light' | 'dark'; // Uygulanan gerçek tema (system seçiliyse cihaz teması uygulanır)
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'system',
  currentTheme: 'light',
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // LocalStorage'den tema tercihini al, yoksa 'system' kullan
  const [theme, setTheme] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme as ThemeType) || 'system';
  });
  
  // Gerçekte uygulanan tema (system ise cihaz tercihine göre)
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');
  
  // Tema değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);
  
  // Tema değişikliklerini gerçek zamanlı izle
  useEffect(() => {
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        setCurrentTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // İlk yüklemede tema belirle
    if (theme === 'system') {
      setCurrentTheme(mediaQuery.matches ? 'dark' : 'light');
    } else {
      setCurrentTheme(theme === 'dark' ? 'dark' : 'light');
    }
    
    // Sistem teması değişikliğini dinle
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    // Temayı HTML kök öğesine uygula
    document.documentElement.setAttribute('data-theme', currentTheme);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme, currentTheme]);
  
  // Tema classını body'e ekle
  useEffect(() => {
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${currentTheme}-theme`);
  }, [currentTheme]);
  
  // Context değerlerini memoize et
  const contextValue = useMemo(() => ({
    theme,
    currentTheme,
    setTheme
  }), [theme, currentTheme]);
  
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
